import { useState } from 'react';
import { Formik, Field, Form } from 'formik';
import timeaway from '../../../network/timeaway';
import { useLeaveTypes } from '../../../hooks/useLeaveTypes';
import Notification from '../../../ui/component/notification';

type Day = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
type PeriodType = 'weeks' | 'months';

const DAYS: Day[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

interface BookRecurringTimeAwayProps {
  /** Most days a week that may be booked; 0 or unset means no limit. */
  allowedWeeklyDays?: number;
  onBooked?: () => void;
}

const ERRORS: Record<string, string> = {
  INVALID_LEAVE_TYPE: 'That leave type cannot be booked.',
  INVALID_WORKING_DAYS: 'Choose at least one weekday.',
  INVALID_PERIOD: 'Check the period: up to 104 weeks or 24 months.',
};

/**
 * Books the same weekdays every week for a period (for example working
 * remotely every Friday for 12 weeks) via PUT /v1/timeaway/recurring.
 * Each day becomes its own pending request; days already booked, public
 * holidays and non-working days are skipped.
 */
const BookRecurringTimeAway = ({
  allowedWeeklyDays = 0,
  onBooked,
}: BookRecurringTimeAwayProps) => {
  const { leaveTypes } = useLeaveTypes();
  const [result, setResult] = useState<{ title: string; message: string; isError: boolean } | null>(
    null,
  );

  return (
    <>
      <Formik
        initialValues={{
          kind: 'working_remotely',
          workingDays: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
          } as Record<Day, boolean>,
          periodValue: 4,
          periodType: 'weeks' as PeriodType,
          periodStartDate: new Date().toISOString().split('T')[0],
        }}
        onSubmit={async (values, actions) => {
          const selectedDays = DAYS.filter((day) => values.workingDays[day]);

          if (selectedDays.length === 0) {
            setResult({ title: 'Nothing booked', message: ERRORS.INVALID_WORKING_DAYS, isError: true });
            return;
          }
          if (allowedWeeklyDays > 0 && selectedDays.length > allowedWeeklyDays) {
            setResult({
              title: 'Nothing booked',
              message: `You can choose up to ${allowedWeeklyDays} day(s) a week.`,
              isError: true,
            });
            return;
          }

          try {
            const response = await timeaway.bookRecurringTimeAway({
              kind: values.kind,
              workingDays: selectedDays,
              period: {
                value: Number(values.periodValue),
                type: values.periodType === 'months' ? 'month' : 'weeks',
                start: new Date(values.periodStartDate),
              },
            });
            const created = response?.createdCount ?? 0;
            const skipped = response?.skippedCount ?? 0;
            setResult({
              title: created ? 'Recurring time away requested' : 'Nothing booked',
              message:
                `${created} day(s) requested, pending approval.` +
                (skipped ? ` ${skipped} day(s) skipped (already booked, or not a working day).` : ''),
              isError: created === 0,
            });
            actions.resetForm();
            onBooked?.();
          } catch (error: any) {
            const status = error?.response?.data?.status;
            setResult({
              title: 'Recurring time away not requested',
              message:
                ERRORS[status] ||
                error?.response?.data?.message ||
                'Unable to submit request. Please try again.',
              isError: true,
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form aria-label="Book recurring time away">
            <div className="overflow-hidden shadow sm:rounded-md">
              <div className="space-y-6 bg-white px-4 py-5 sm:p-6">
                <div>
                  <label htmlFor="recurring-kind" className="block text-sm font-medium text-gray-700">
                    Time away type
                  </label>
                  <Field
                    as="select"
                    id="recurring-kind"
                    name="kind"
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                  >
                    {leaveTypes
                      .filter((type) => type.active)
                      .map((type) => (
                        <option key={type.key} value={type.key}>
                          {type.name}
                        </option>
                      ))}
                  </Field>
                </div>

                <fieldset>
                  <legend className="mb-2 block text-sm font-medium text-gray-700">
                    Every
                    {allowedWeeklyDays > 0 ? ` (up to ${allowedWeeklyDays} a week)` : ''}
                  </legend>
                  <div className="flex flex-wrap gap-4">
                    {DAYS.map((day) => (
                      <label key={day} htmlFor={`recurring-${day}`} className="flex items-center gap-2 text-sm capitalize">
                        <Field
                          id={`recurring-${day}`}
                          name={`workingDays.${day}`}
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-600"
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="grid grid-cols-12 gap-2">
                  <label htmlFor="period-start-date" className="col-span-12 text-sm font-medium text-gray-700 sm:col-span-5">
                    Starting
                    <Field
                      type="date"
                      id="period-start-date"
                      name="periodStartDate"
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                    />
                  </label>
                  <label htmlFor="period-value" className="col-span-6 text-sm font-medium text-gray-700 sm:col-span-3">
                    For
                    <Field
                      type="number"
                      id="period-value"
                      name="periodValue"
                      min="1"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                    />
                  </label>
                  <label htmlFor="period-type" className="col-span-6 text-sm font-medium text-gray-700 sm:col-span-4">
                    <span className="sr-only">Period unit</span>
                    <Field
                      as="select"
                      id="period-type"
                      name="periodType"
                      className="mt-6 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                    >
                      <option value="weeks">weeks</option>
                      <option value="months">months</option>
                    </Field>
                  </label>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 text-right sm:px-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Send recurring request
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <Notification
        title={result?.title ?? ''}
        message={result?.message ?? ''}
        open={Boolean(result)}
        close={() => setResult(null)}
        isError={result?.isError}
      />
    </>
  );
};

export default BookRecurringTimeAway;
